import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import logging from "../utils/logging";
import Header from "../components/Header";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Users, Shield, UserX, UserCheck, Search, Ban, Award, MessageSquare, Download, UserPlus, FileUp, QrCode } from "lucide-react";
import { Input } from "../components/ui/input";
import { Link } from "react-router-dom";
import { downloadCSV, stampedFilename } from "../utils/csvExport";
import AddUserDialog from "../components/AddUserDialog";
import BulkImportDialog from "../components/BulkImportDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const roleBadgeStyles = {
  system_admin: "bg-black text-white hover:bg-black",
  govt_admin: "bg-gray-800 text-white hover:bg-gray-800",
  user: "bg-gray-200 text-gray-700 hover:bg-gray-200",
};

function formatDate(isoStr) {
  if (!isoStr) return "-";
  return new Date(isoStr).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function AdminUsersPage() {
  const { t } = useTranslation();
  const roleLabels = { system_admin: t("admin_users.role_system"), govt_admin: t("admin_users.role_govt"), user: t("admin_users.role_citizen") };
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [estateFilter, setEstateFilter] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users/list`, { withCredentials: true });
      setUsers(res.data);
    } catch (err) {
      logging.error("Failed to fetch users", err);
    }
    setLoading(false);
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(res.data);
    } catch (err) {
      logging.error("Failed to fetch notifications", err);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchNotifications();
  }, [fetchUsers, fetchNotifications]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.patch(`${API}/users/${userId}/role`, { role: newRole }, { withCredentials: true });
      fetchUsers();
      toast.success(t("admin_users.role_updated"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("admin_users.role_failed"));
    }
  };

  const handleToggleActive = async (userId, isDeactivated) => {
    try {
      await axios.patch(`${API}/users/${userId}/deactivate`, {}, { withCredentials: true });
      fetchUsers();
      toast.success(isDeactivated ? t("admin_users.reactivated_toast") : t("admin_users.deactivated_toast"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("admin_users.status_failed"));
    }
  };

  const handleClearSanction = async (userId) => {
    if (!window.confirm(t("admin_users.clear_sanction_confirm"))) return;
    try {
      await axios.post(`${API}/users/${userId}/clear-sanction`, {}, { withCredentials: true });
      fetchUsers();
      toast.success(t("admin_users.sanction_cleared"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("admin_users.sanction_failed"));
    }
  };

  const [resendingId, setResendingId] = useState(null);
  const handleResendQr = async (userId) => {
    setResendingId(userId);
    try {
      const r = await axios.post(`${API}/users/${userId}/resend-qr`, {}, { withCredentials: true });
      toast.success(`QR email re-sent to ${r.data.email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not re-send the email");
    } finally {
      setResendingId(null);
    }
  };

  const isSystemAdmin = user?.role === "system_admin";
  const estates = [...new Set(users.map((u) => u.estate).filter(Boolean))].sort();
  const filtered = users.filter((u) => {
    if (estateFilter !== "all" && u.estate !== estateFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.role?.includes(q) || u.estate?.toLowerCase().includes(q);
  });

  const handleExportSanctions = useCallback(() => {
    const sanctioned = users.filter((u) => u.sanctioned);
    if (!sanctioned.length) {
      toast.message(t("admin_users.no_sanctions"));
      return;
    }
    const n = downloadCSV({
      filename: stampedFilename("guard_sanctions"),
      columns: [
        { key: "user_id", header: "User ID" },
        { key: "name", header: "Name" },
        { key: "email", header: "Email" },
        { key: "role", header: "Role" },
        { key: "sanction_reason", header: "Reason" },
        { key: (r) => (r.sanctioned_at ? new Date(r.sanctioned_at).toLocaleString() : ""), header: "Sanctioned At" },
        { key: "trust_score", header: "Trust Score" },
        { key: (r) => (r.deactivated ? "deactivated" : "active"), header: "Account Status" },
      ],
      data: sanctioned,
    });
    toast.success(t(n === 1 ? "admin_users.exported_sanctions_one" : "admin_users.exported_sanctions", { n }));
  }, [users, t]);

  return (
    <div className="min-h-screen bg-[#F3F4F6]" data-testid="admin-users-page">
      <Header notifications={notifications} onNotificationRead={fetchNotifications} />

      <div className="p-4 md:p-6 max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Users className="w-7 h-7 text-gray-900" strokeWidth={2} />
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tighter text-gray-900">{t("admin_users.title")}</h1>
              <p className="text-sm text-gray-500 font-body">{t("admin_users.count", { n: users.length })}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isSystemAdmin && (
              <>
                <Button
                  size="sm"
                  onClick={() => setAddOpen(true)}
                  className="rounded-sm bg-black hover:bg-gray-800 text-white text-xs font-bold"
                  data-testid="add-user-btn"
                >
                  <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Add user
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setImportOpen(true)}
                  className="rounded-sm border-gray-300 text-gray-700 hover:bg-gray-50 text-xs font-bold"
                  data-testid="bulk-import-btn"
                >
                  <FileUp className="w-3.5 h-3.5 mr-1.5" /> Import CSV
                </Button>
              </>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportSanctions}
              className="rounded-sm border-gray-300 text-gray-700 hover:bg-gray-50 text-xs font-bold"
              data-testid="export-sanctions-csv"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" /> {t("admin_users.sanctions_csv")}
            </Button>
            <Link
              to="/admin/appeals"
              className="text-xs font-bold text-gray-700 hover:text-black border border-gray-300 hover:bg-gray-100 rounded-sm px-3 py-1.5 inline-flex items-center gap-1.5"
              data-testid="link-admin-appeals"
            >
              <MessageSquare className="w-3.5 h-3.5" /> {t("admin_users.appeals")}
            </Link>
            <div className="relative w-64">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
              <Input
                placeholder={t("admin_users.search_placeholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 rounded-sm border-gray-300 font-body text-sm"
                data-testid="user-search"
              />
            </div>
            {estates.length > 0 && (
              <Select value={estateFilter} onValueChange={setEstateFilter}>
                <SelectTrigger className="h-9 w-44 rounded-sm border-gray-300 font-body text-xs" data-testid="estate-filter">
                  <SelectValue placeholder="All estates" />
                </SelectTrigger>
                <SelectContent className="rounded-sm">
                  <SelectItem value="all" className="text-sm font-body">All estates</SelectItem>
                  {estates.map((e) => (
                    <SelectItem key={e} value={e} className="text-sm font-body">{e}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid="users-table">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="text-xs uppercase tracking-widest font-bold text-gray-500 py-2 px-3">{t("admin_users.col_user")}</TableHead>
                <TableHead className="text-xs uppercase tracking-widest font-bold text-gray-500 py-2 px-3">{t("common.email")}</TableHead>
                <TableHead className="text-xs uppercase tracking-widest font-bold text-gray-500 py-2 px-3">{t("common.role")}</TableHead>
                <TableHead className="text-xs uppercase tracking-widest font-bold text-gray-500 py-2 px-3 hidden sm:table-cell">{t("admin_users.col_joined")}</TableHead>
                <TableHead className="text-xs uppercase tracking-widest font-bold text-gray-500 py-2 px-3">{t("common.status")}</TableHead>
                {isSystemAdmin && (
                  <TableHead className="text-xs uppercase tracking-widest font-bold text-gray-500 py-2 px-3 w-28">{t("common.actions")}</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12 text-sm text-gray-400 font-body">{t("common.loading")}</TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12 text-sm text-gray-400 font-body">{t("admin_users.no_users")}</TableCell>
                </TableRow>
              ) : (
                filtered.map((u, idx) => {
                  const isSelf = u.user_id === user?.user_id;
                  const isDeactivated = u.deactivated;
                  return (
                    <TableRow key={u.user_id} className={`${idx % 2 === 1 ? "bg-gray-50" : ""} ${isDeactivated ? "opacity-50" : ""}`} data-testid={`user-row-${u.user_id}`}>
                      <TableCell className="py-2 px-3">
                        <div className="flex items-center gap-2">
                          <img
                            src={u.picture || `https://ui-avatars.com/api/?name=${u.name || "U"}&background=111827&color=fff&size=28`}
                            alt={u.name}
                            className="w-7 h-7 rounded-full border border-gray-200"
                          />
                          <span className="text-sm font-medium text-gray-800 font-body">{u.name}</span>
                          {u.estate && (
                            <Badge
                              className="text-[9px] px-1.5 py-0 rounded-sm bg-blue-50 text-blue-700 hover:bg-blue-50 border border-blue-200 cursor-pointer"
                              onClick={() => setEstateFilter(u.estate)}
                              title={`Filter by ${u.estate}`}
                              data-testid={`estate-badge-${u.user_id}`}
                            >
                              {u.estate}
                            </Badge>
                          )}
                          {isSelf && <span className="text-[9px] text-gray-400 font-body">{t("common.you")}</span>}
                        </div>
                      </TableCell>
                      <TableCell className="py-2 px-3 text-xs text-gray-600 font-body">{u.email}</TableCell>
                      <TableCell className="py-2 px-3">
                        {isSystemAdmin && !isSelf ? (
                          <Select value={u.role} onValueChange={(val) => handleRoleChange(u.user_id, val)}>
                            <SelectTrigger className="h-7 w-32 rounded-sm border-gray-300 font-body text-[11px]" data-testid={`role-select-${u.user_id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="rounded-sm">
                              <SelectItem value="user" className="text-sm font-body">{t("admin_users.role_citizen")}</SelectItem>
                              <SelectItem value="govt_admin" className="text-sm font-body">{t("admin_users.role_govt")}</SelectItem>
                              <SelectItem value="system_admin" className="text-sm font-body">{t("admin_users.role_system")}</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge className={`text-[10px] px-1.5 py-0 rounded-sm ${roleBadgeStyles[u.role] || roleBadgeStyles.user}`}>
                            {roleLabels[u.role] || t("admin_users.role_citizen")}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="py-2 px-3 hidden sm:table-cell text-xs text-gray-500 font-body">
                        {formatDate(u.created_at)}
                      </TableCell>
                      <TableCell className="py-2 px-3">
                        <div className="flex items-center gap-1 flex-wrap">
                          {isDeactivated ? (
                            <Badge className="text-[10px] px-1.5 py-0 rounded-sm bg-red-100 text-red-800 hover:bg-red-100">{t("admin_users.deactivated")}</Badge>
                          ) : (
                            <Badge className="text-[10px] px-1.5 py-0 rounded-sm bg-green-100 text-green-800 hover:bg-green-100">{t("admin_users.active")}</Badge>
                          )}
                          {u.sanctioned && (
                            <Badge className="text-[10px] px-1.5 py-0 rounded-sm bg-gray-800 text-white hover:bg-gray-800" title={t("admin_users.sanctioned")} data-testid={`sanctioned-${u.user_id}`}>
                              <Ban className="w-2.5 h-2.5 mr-0.5" /> {t("admin_users.sanctioned")}
                            </Badge>
                          )}
                          {typeof u.trust_score === "number" && u.trust_score >= 80 && (
                            <Badge className="text-[10px] px-1.5 py-0 rounded-sm bg-green-50 text-green-700 hover:bg-green-50 border border-green-200" title={`Trust score: ${u.trust_score}`}>
                              <Award className="w-2.5 h-2.5 mr-0.5" /> {u.trust_score}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      {isSystemAdmin && (
                        <TableCell className="py-2 px-3">
                          {!isSelf && (
                            <div className="flex items-center gap-1 flex-wrap">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleActive(u.user_id, isDeactivated)}
                                className={`h-6 text-[10px] rounded-sm px-2 ${
                                  isDeactivated
                                    ? "text-green-700 hover:text-green-800 hover:bg-green-50"
                                    : "text-red-600 hover:text-red-700 hover:bg-red-50"
                                }`}
                                data-testid={`toggle-active-${u.user_id}`}
                              >
                                {isDeactivated ? (
                                  <><UserCheck className="w-3 h-3 mr-1" /> {t("admin_users.activate")}</>
                                ) : (
                                  <><UserX className="w-3 h-3 mr-1" /> {t("admin_users.deactivate")}</>
                                )}
                              </Button>
                              {u.email && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleResendQr(u.user_id)}
                                  disabled={resendingId === u.user_id}
                                  title="Re-send their Scan2Call QR welcome email"
                                  className="h-6 text-[10px] rounded-sm px-2 text-gray-600 hover:text-black hover:bg-gray-100"
                                  data-testid={`resend-qr-${u.user_id}`}
                                >
                                  <QrCode className="w-3 h-3 mr-1" /> Email QR
                                </Button>
                              )}
                              {u.sanctioned && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleClearSanction(u.user_id)}
                                  className="h-6 text-[10px] rounded-sm px-2 text-blue-700 hover:text-blue-800 hover:bg-blue-50"
                                  data-testid={`clear-sanction-${u.user_id}`}
                                >
                                  <Ban className="w-3 h-3 mr-1" /> {t("admin_users.clear_sanction")}
                                </Button>
                              )}
                            </div>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <AddUserDialog open={addOpen} onClose={() => setAddOpen(false)} onCreated={fetchUsers} />
      <BulkImportDialog open={importOpen} onClose={() => setImportOpen(false)} onImported={fetchUsers} />
    </div>
  );
}
